import { useEffect, useRef, useState } from 'react';
import { Panel } from './Panel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const url = (path) => `${API}/professor/audio/${path}`;
const post = (p, body) => fetch(`${API}${p}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined }).then((r) => (r.ok ? r.json() : Promise.reject(r)));

function groupLines(lines) {
  const groups = [{ key: 'opening', title: 'Opening + prologue', ids: [] }, { key: 'ritual', title: 'Ritual', ids: [] }, { key: 'rules', title: 'Rules', ids: [] }];
  const levels = [];
  const tail = { wrong: { key: 'wrong', title: 'Wrong attempt · encouragement', ids: [] }, volunteer: { key: 'volunteer', title: 'Volunteers', ids: [] }, calm: { key: 'calm', title: 'Calm-down game', ids: [] }, finale: { key: 'finale', title: 'Finale · ze robin brooch', ids: [] } };
  lines.forEach((l) => {
    const [f, kind] = l.id.split('.');
    if (l.id === 'opening' || f === 'prologue') groups[0].ids.push(l.id);
    else if (f === 'ritual') groups[1].ids.push(l.id);
    else if (f === 'rules' || l.id === 'fear.tease') groups[2].ids.push(l.id);
    else if (['instruct', 'nudge', 'interpret', 'memory'].includes(kind)) {
      let g = levels.find((x) => x.feeling === f);
      if (!g) levels.push((g = { key: f, feeling: f, title: `Level ${levels.length + 1} · ${f}`, ids: [] }));
      g.ids.push(l.id);
    } else if (f === 'encourage') tail.wrong.ids.push(l.id);
    else if (tail[f]) tail[f].ids.push(l.id);
    else if (l.id === 'closing') tail.finale.ids.push(l.id);
  });
  return [...groups, ...levels, ...Object.values(tail)];
}

const LineRow = ({ line, playing, onPlay, pending, busy, onRetake, onKeep, onRevert }) => (
  <div data-testid={`vc-line-${line.id}`} data-playing={playing} className={`flex items-start gap-3 border-b border-white/5 py-1.5 ${playing ? 'bg-[var(--spore)]/10' : ''}`}>
    <button data-testid={`vc-play-${line.id}`} className="btn-lab px-2 py-0.5 text-[0.6rem]" disabled={!line.audio_path} onClick={() => onPlay(line.audio_path, line.id)}>▶</button>
    <div className="flex-1 text-xs">
      <span className="text-[var(--muted)]">{line.id.split('.').pop()}</span> · {line.text}
      {line.guess && <span className="text-[var(--amber)]"> · {line.guess}</span>}
      {line.retaken && <span data-testid={`vc-retaken-${line.id}`} className="ml-2 border border-[var(--amber)] px-1 text-[0.55rem] text-[var(--amber)]">RE-TAKEN</span>}
      {line.voice_source !== 'elevenlabs' && <span className="ml-2 text-[var(--amber)]">[{line.voice_source}]</span>}
    </div>
    {pending ? (
      <div className="flex gap-1">
        <span className="self-center text-[0.6rem] text-[var(--muted)]">{pending.credits_used} cr</span>
        <button data-testid={`vc-play-new-${line.id}`} className="btn-lab px-2 py-0.5 text-[0.6rem]" onClick={() => onPlay(pending.retake_path, `${line.id}#new`)}>▶ new</button>
        <button data-testid={`vc-keep-${line.id}`} className="btn-lab px-2 py-0.5 text-[0.6rem]" onClick={() => onKeep(line.id)}>Keep new</button>
        <button data-testid={`vc-revert-${line.id}`} className="btn-lab px-2 py-0.5 text-[0.6rem]" onClick={() => onRevert(line.id)}>Revert</button>
      </div>
    ) : (
      <button data-testid={`vc-retake-${line.id}`} className="btn-lab px-2 py-0.5 text-[0.6rem]" disabled={busy} onClick={() => onRetake(line.id)}>{busy === line.id ? 'Voicing…' : 'Re-take'}</button>
    )}
  </div>
);

const AbBlock = ({ ab, model, setModel, onPlay, playingId }) => (
  <div data-testid="vc-ab" className="mb-4 border border-[var(--hair)] p-3">
    <div className="mb-2 flex items-center justify-between">
      <div className="label-eyebrow">MODEL A/B · 3 TEST LINES</div>
      <div className="flex gap-1">
        {Object.entries(ab.profiles).map(([k, label], i) => (
          <button key={k} data-testid={`vc-ab-model-${k}`} className={`btn-lab px-2 py-0.5 text-[0.6rem] ${model === k ? 'active' : ''}`} title={label} onClick={() => setModel(k)}>{i === 0 ? 'A' : 'B'} · {label.split(' · ')[0]}{k === ab.chosen ? ' (in use)' : ''}</button>
        ))}
      </div>
    </div>
    {ab.lines.map((l) => (
      <div key={l.id} className={`flex items-center gap-3 py-1 text-xs ${playingId === `${l.id}@${model}` ? 'text-[var(--spore)]' : ''}`}>
        <button data-testid={`vc-ab-play-${l.id}`} className="btn-lab px-2 py-0.5 text-[0.6rem]" onClick={() => onPlay(l.takes[model], `${l.id}@${model}`)}>▶</button>
        <span>{l.speech[model]}</span>
      </div>
    ))}
    <div className="mt-1 text-[0.6rem] text-[var(--dim)]">{ab.profiles[model]}</div>
  </div>
);

export const VoiceCheckPanel = () => {
  const [script, setScript] = useState(null);
  const [ab, setAb] = useState(null);
  const [model, setModel] = useState('v2_stable');
  const [playingId, setPlayingId] = useState(null);
  const [pending, setPending] = useState({});
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const audio = useRef(null);
  const stopRef = useRef(false);
  const load = () => fetch(`${API}/professor/script/current`).then((r) => r.json()).then((d) => {
    setScript(d);
    setPending(Object.fromEntries(d.lines.filter((l) => l.retake_path).map((l) => [l.id, { retake_path: l.retake_path, credits_used: '?' }])));
  });
  useEffect(() => {
    load();
    fetch(`${API}/voice/ab`).then((r) => (r.ok ? r.json() : null)).then((d) => d && setAb(d));
    return () => audio.current && audio.current.pause();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const play = (path, id) => new Promise((resolve) => {
    if (audio.current) audio.current.pause();
    const a = new Audio(url(path));
    audio.current = a;
    setPlayingId(id);
    const done = () => {
      setPlayingId((p) => (p === id ? null : p));
      resolve();
    };
    a.onended = done;
    a.onerror = done;
    a.onpause = done;
    a.play().catch(done);
  });
  const playMany = async (lines) => {
    stopRef.current = false;
    for (const l of lines) {
      if (stopRef.current) break;
      if (l.audio_path) await play(l.audio_path, l.id); // eslint-disable-line no-await-in-loop
    }
  };
  const stop = () => {
    stopRef.current = true;
    if (audio.current) audio.current.pause();
  };
  const retake = async (id) => {
    setBusy(id);
    setErr(null);
    try {
      const r = await post(`/professor/line/${id}/retake`);
      setPending((p) => ({ ...p, [id]: r }));
    } catch {
      setErr(`Re-take of ${id} failed`);
    }
    setBusy(null);
  };
  const settle = async (id, action) => {
    await post(`/professor/line/${id}/${action}`);
    setPending(({ [id]: _, ...rest }) => rest);
    load();
  };

  if (!script) return null;
  const byId = Object.fromEntries(script.lines.map((l) => [l.id, l]));
  const ordered = script.lines;
  return (
    <Panel
      testId="voice-check-panel"
      code="07 / VOICE CHECK"
      title={`Voice Check · ${script.script_id} · ${script.lines.length} lines · ${script.voice_profile || 'v3'}`}
      right={
        <div className="flex gap-2">
          <button data-testid="vc-play-all" className="btn-lab px-3 py-1 text-[0.65rem]" onClick={() => playMany(ordered)}>Play all</button>
          <button data-testid="vc-play-interpretations" className="btn-lab px-3 py-1 text-[0.65rem]" onClick={() => playMany(ordered.filter((l) => l.id.endsWith('.interpret')))}>Play interpretations only</button>
          <button data-testid="vc-play-story" className="btn-lab px-3 py-1 text-[0.65rem]" onClick={() => playMany(ordered.filter((l) => /^(prologue|finale|closing)|\.memory$/.test(l.id)))}>Play story only</button>
          <button data-testid="vc-stop" className="btn-lab px-3 py-1 text-[0.65rem]" onClick={stop}>Stop</button>
        </div>
      }
    >
      {err && <div data-testid="vc-error" className="mb-2 text-xs text-[var(--amber)]">{err}</div>}
      {ab && <AbBlock ab={ab} model={model} setModel={setModel} onPlay={play} playingId={playingId} />}
      <div data-testid="vc-now-playing" className="mb-2 text-xs text-[var(--muted)]">NOW PLAYING: {playingId || '—'}</div>
      <div className="max-h-[560px] overflow-y-auto pr-2">
        {groupLines(script.lines).map((g) => (
          <div key={g.key} data-testid={`vc-group-${g.key}`} className="mb-3">
            <div className="label-eyebrow mb-1">{g.title}</div>
            {g.ids.filter((id) => byId[id]).map((id) => (
              <LineRow key={id} line={byId[id]} playing={playingId === id} onPlay={play} pending={pending[id]} busy={busy} onRetake={retake} onKeep={(x) => settle(x, 'keep')} onRevert={(x) => settle(x, 'revert')} />
            ))}
          </div>
        ))}
      </div>
    </Panel>
  );
};
